/**
 * @engine/core - LevelSerializer
 * 
 * Serialization and deserialization for levels.
 * Handles saving/loading level data to/from JSON.
 */

import { Level, EditorFolder } from './Level';
import { GameObject } from './GameObject';
import type { GUID, Tag, Layer, TransformData } from './types';
import { generateGUID } from './types';
import { getComponentByTypeId } from './attributes';

/** Serialize a component's data, excluding any `@runtime`-marked (runtime-only) properties. */
function serializeComponentValues(
  typeId: string,
  data: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!data) return {};
  const runtimeFields = getComponentByTypeId(typeId)?.runtimeFields;
  if (!runtimeFields || runtimeFields.size === 0) return { ...data };
  const out: Record<string, unknown> = {};
  for (const key in data) {
    if (!runtimeFields.has(key)) out[key] = data[key];
  }
  return out;
}

// ============================================================================
// TYPES
// ============================================================================

/**
 * Serialized component data
 */
export interface SerializedComponent {
  /** Component type ID */
  typeId: string;
  /** Component field values */
  values: Record<string, unknown>;
}

/**
 * Serialized GameObject data
 */
export interface SerializedGameObject {
  /** GUID */
  guid: GUID;
  /** Display name */
  name: string;
  /** Tag */
  tag: number;
  /** Layer */
  layer: number;
  /** Active state */
  active: boolean;
  /** Static flag */
  isStatic: boolean;
  /** Transform data */
  transform: TransformData;
  /** Component data */
  components: SerializedComponent[];
  /** Children (recursively serialized) */
  children: SerializedGameObject[];
}

/**
 * Serialized editor folder
 */
export interface SerializedEditorFolder {
  /** Folder ID */
  id: GUID;
  /** Display name */
  name: string;
  /** Parent folder ID (null = root level) */
  parentId: GUID | null;
  /** Custom icon name (lucide icon name) */
  icon?: string;
  /** Custom color (hex string, e.g. "#ff5500") */
  color?: string;
  /** Notes/comments for this folder */
  notes?: string;
  /** Sort order key (fractional indexing) */
  order: string;
  /** Child GameObject GUIDs */
  childObjects: GUID[];
  /** Child folder IDs */
  childFolders: GUID[];
}

/**
 * Serialized level data
 */
export interface SerializedLevel {
  /** Format version */
  version: number;
  /** Level GUID */
  guid: GUID;
  /** Level name */
  name: string;
  /** Root objects */
  rootObjects: SerializedGameObject[];
  /** Level settings */
  settings?: {
    backgroundColor?: number;
    gravity?: { x: number; y: number };
  };
  /** Editor metadata (legacy — no longer written so saves stay byte-identical; kept so old .lvl files parse) */
  editorMeta?: {
    savedAt?: number;
    lastOpenedAt?: number;
  };
  /** Editor folders */
  editorFolders?: SerializedEditorFolder[];
  /** Root object ordering (GUID -> order key) */
  rootObjectOrders?: Record<GUID, string>;
}

/** Current serialization format version */
export const LEVEL_FORMAT_VERSION = 1;

// ============================================================================
// SERIALIZATION
// ============================================================================

/**
 * Serialize a GameObject (including children)
 */
export function serializeGameObject(go: GameObject, level: Level): SerializedGameObject {
  const transform = go.transform;
  
  // Serialize transform
  const transformData: TransformData = {
    localPosition: {
      x: transform.localPosition.x,
      y: transform.localPosition.y,
      z: transform.localPosition.z,
    },
    localRotation: transform.localRotation,
    localScale: {
      x: transform.localScale.x,
      y: transform.localScale.y,
    },
  };
  
  // Serialize components
  const components: SerializedComponent[] = [];
  for (const compState of go.getAllComponents()) {
    components.push({
      typeId: compState.typeId,
      values: serializeComponentValues(compState.typeId, compState.data),
    });
  }
  
  // Serialize children recursively
  const children: SerializedGameObject[] = [];
  for (const childTransform of transform.children) {
    const childGo = level.findById(childTransform.gameObjectId);
    if (childGo) {
      children.push(serializeGameObject(childGo, level));
    }
  }
  
  return {
    guid: go.guid,
    name: go.name,
    tag: go.tag,
    layer: go.layer,
    active: go.activeSelf,
    isStatic: go.isStatic,
    transform: transformData,
    components,
    children,
  };
}

/** Deep-copy a serialized GameObject subtree with fresh GUIDs (for duplication/instantiation). */
function regenerateGuids(data: SerializedGameObject): SerializedGameObject {
  return {
    ...data,
    guid: generateGUID(),
    transform: structuredClone(data.transform),
    components: data.components.map((c) => ({ typeId: c.typeId, values: structuredClone(c.values) })),
    children: data.children.map(regenerateGuids),
  };
}

/**
 * Clone a live GameObject (with children + component data, fresh GUIDs) into the same level.
 * The runtime instantiate primitive: backs `engine.scene.instantiate` and editor duplication.
 * Defaults to the source's parent; the clone lands at the source's local transform.
 */
export function cloneGameObject(source: GameObject, level: Level, parent?: GameObject): GameObject {
  const snapshot = regenerateGuids(serializeGameObject(source, level));
  const parentGo =
    parent ??
    (source.transform.parent ? level.findById(source.transform.parent.gameObjectId) ?? undefined : undefined);
  return deserializeGameObject(snapshot, level, parentGo);
}

/**
 * Serialize a level
 */
export function serializeLevel(level: Level): SerializedLevel {
  const rootObjects = level.getRootObjects().map(go => serializeGameObject(go, level));
  
  // Serialize folders
  const foldersMap = level.getAllFolders();
  let editorFolders: SerializedEditorFolder[] | undefined;
  
  if (foldersMap.size > 0) {
    editorFolders = [];
    for (const folder of foldersMap.values()) {
      editorFolders.push({
        id: folder.id,
        name: folder.name,
        parentId: folder.parentId,
        icon: folder.icon,
        color: folder.color,
        notes: folder.notes,
        order: folder.order,
        childObjects: [...folder.childObjects],
        childFolders: [...folder.childFolders],
      });
    }
  }
  
  // Serialize root object orders
  const rootObjectOrdersMap = level.getRootObjectOrders();
  let rootObjectOrders: Record<GUID, string> | undefined;
  if (rootObjectOrdersMap.size > 0) {
    rootObjectOrders = Object.fromEntries(rootObjectOrdersMap);
  }
  
  return {
    version: LEVEL_FORMAT_VERSION,
    guid: level.guid,
    name: level.name,
    rootObjects,
    editorFolders,
    rootObjectOrders,
  };
}

// ============================================================================
// DESERIALIZATION
// ============================================================================

/**
 * Migrate legacy component shapes on deserialization. The unified `core.Script` component
 * replaced `core.Redprint`; old levels (editor + player) are upgraded transparently on load.
 */
function migrateLegacyComponentData(
  typeId: string,
  values: Record<string, unknown>,
): { typeId: string; values: Record<string, unknown> } {
  if (typeId === "core.Redprint") {
    return {
      typeId: "core.Script",
      values: {
        scriptType: "redprint",
        assetId: values.assetId ?? "",
        enabled: values.enabled ?? true,
        variables: values.variables ?? {},
        priority: values.priority ?? 0,
      },
    };
  }
  return { typeId, values };
}

/**
 * Deserialize a GameObject into a level
 */
export function deserializeGameObject(
  data: SerializedGameObject,
  level: Level,
  parent?: GameObject
): GameObject {
  // Create GameObject with config
  const go = level.createGameObjectWithConfig({
    guid: data.guid,
    name: data.name,
    tag: data.tag as Tag,
    layer: data.layer as Layer,
    active: data.active,
    isStatic: data.isStatic,
    transform: data.transform,
  });
  
  // Set parent and update root status
  if (parent) {
    go.transform.parent = parent.transform;
    level.updateRootStatus(go);
  }
  
  // Restore components (migrating any legacy component shapes on the way in).
  for (const compData of data.components) {
    const migrated = migrateLegacyComponentData(compData.typeId, compData.values);
    go.addComponentData(migrated.typeId, migrated.values);
  }
  
  // Deserialize children recursively
  for (const childData of data.children) {
    deserializeGameObject(childData, level, go);
  }
  
  return go;
}

/**
 * Deserialize a level from JSON data
 */
export function deserializeLevel(data: SerializedLevel): Level {
  // Validate version
  if (data.version > LEVEL_FORMAT_VERSION) {
    console.warn(`[LevelSerializer] Level format version ${data.version} is newer than supported ${LEVEL_FORMAT_VERSION}`);
  }
  
  // Create level
  const level = new Level({
    guid: data.guid,
    name: data.name,
  });
  
  // Deserialize root objects
  for (const objData of data.rootObjects) {
    deserializeGameObject(objData, level);
  }
  
  // Restore folders
  if (data.editorFolders && data.editorFolders.length > 0) {
    const foldersMap = new Map<GUID, EditorFolder>();
    for (const folderData of data.editorFolders) {
      foldersMap.set(folderData.id, {
        id: folderData.id,
        name: folderData.name,
        parentId: folderData.parentId,
        icon: folderData.icon,
        color: folderData.color,
        notes: folderData.notes,
        order: folderData.order ?? 'a0', // Default order for backwards compat
        childObjects: [...folderData.childObjects],
        childFolders: [...folderData.childFolders],
      });
    }
    level.setAllFolders(foldersMap);
  }
  
  // Restore root object orders
  if (data.rootObjectOrders) {
    for (const [guid, order] of Object.entries(data.rootObjectOrders)) {
      level.setRootObjectOrder(guid, order);
    }
  }
  
  // Ensure _rootObjects is correct after all objects created
  level.updateHierarchy();
  
  return level;
}

// ============================================================================
// JSON HELPERS
// ============================================================================

/**
 * Serialize level to JSON string
 */
export function levelToJSON(level: Level, pretty: boolean = true): string {
  const data = serializeLevel(level);
  return pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);
}

/**
 * Deserialize level from JSON string
 */
export function levelFromJSON(json: string): Level {
  const data = JSON.parse(json) as SerializedLevel;
  return deserializeLevel(data);
}
